import type { Block } from "@/lib/api";

/**
 * IndexedDB write-through buffer for the Flow editor's continuous sync.
 *
 * Every edit lands here synchronously off the interaction path; the
 * background sync worker drains it to the server. This is a
 * latency/offline cache — Postgres remains the single source of truth.
 * Hand-rolled (no `idb` dependency) since it's a single key-value store.
 * See architecture/2026-05-21-flow-editor-save-model.md.
 */

const DB_NAME = "lilia-editor";
const DB_VERSION = 1;
const STORE = "working-copy";

export interface BufferedDoc {
  documentId: string;
  title: string;
  blocks: Block[];
  /** Server version the buffer was last reconciled against. */
  version: number;
  /** `editVersion` of the store at the time of this write (monotonic). */
  editSeq: number;
  /** `editSeq` confirmed synced to the server. */
  syncedSeq: number;
  updatedAt: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "documentId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function run<T>(
  mode: IDBTransactionMode,
  op: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const req = op(tx.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

/** Read the buffered working copy for a document, if any. */
export function readBuffer(documentId: string): Promise<BufferedDoc | undefined> {
  return run<BufferedDoc | undefined>(
    "readonly",
    (s) => s.get(documentId) as IDBRequest<BufferedDoc | undefined>,
  );
}

/** Write-through: persist the latest working copy for a document. */
export async function writeBuffer(doc: BufferedDoc): Promise<void> {
  await run("readwrite", (s) => s.put(doc));
}

/** Drop a document's buffer. */
export async function clearBuffer(documentId: string): Promise<void> {
  await run("readwrite", (s) => s.delete(documentId));
}
